import styled, { css } from 'styled-components';

import { ColorName } from 'types';

import { getHoverColor, pickColor } from 'themes/helpers/pickColor';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';
import {
  CODE_COLUMN_WIDTH,
  COLUMN_GAP,
  TOGGLE_COLUMN_WIDTH,
} from 'components/OrderAddressBlocks/layout';

const Container = styled.div`
  /* Room for the code + name columns of the address blocks. */
  width: min(84rem, 92vw);
`;

/** Commercial Zone select in the code column, above the address blocks. */
const ZoneField = styled.div<{ $color?: ColorName }>`
  width: ${CODE_COLUMN_WIDTH};
  max-width: 100%;
  margin-left: calc(${TOGGLE_COLUMN_WIDTH} + ${COLUMN_GAP});

  /* Catalogue colour on the label, value and arrow, as in the address blocks. */
  ${({ $color }) =>
    $color &&
    css`
      label,
      [class*='singleValue'],
      [class*='placeholder'],
      [class*='indicatorContainer'] {
        color: ${pickColor($color)};
      }
    `};
`;

const Title = styled(Headline)<{ $color?: ColorName }>`
  padding: 1.5rem 1.5rem 0;
  color: ${({ $color }) => pickColor($color || 'black')};
`;

const Description = styled.p`
  padding: 0 1.5rem;
  margin: 0;
  font-size: 1.4rem;
  color: ${pickColor('black')};
  line-height: 1.4;
`;

const Form = styled.form`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
`;

/** Cancel in the catalogue colour too: white with a coloured border and text. */
const CancelButton = styled(Button)<{ $color?: ColorName }>`
  ${({ $color }) =>
    $color &&
    css`
      background-color: ${pickColor('white')};
      border: 0.1rem solid ${pickColor($color)};
      color: ${pickColor($color)};

      &:hover {
        background-color: ${pickColor($color)};
        color: ${pickColor('white')};
      }
    `};
`;

/** Catalogue colour like the New Order "Create" button. */
const ConfirmButton = styled(Button)<{ $color?: ColorName }>`
  ${({ $color }) =>
    $color &&
    css`
      background-color: ${pickColor($color)};

      &:hover {
        background-color: ${pickColor(getHoverColor($color))};
      }
    `};
`;

const SuccessIcon = styled.span`
  width: 30px;
  height: 40px;
  display: inline-block;
`;

const SuccessTitle = styled(Headline)`
  padding: 1.5rem;
  color: ${pickColor('greenStatus')};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

const SuccessInfo = styled(Headline)`
  padding: 1.5rem 0 4rem 1.5rem;
  border-top: 0.1rem solid ${pickColor('borderSemiLight')};
  border-bottom: 0.1rem solid ${pickColor('borderSemiLight')};
`;

const SuccessActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 2rem;
  padding: 1.5rem;
`;

export {
  ButtonWrapper,
  CancelButton,
  ConfirmButton,
  Container,
  Description,
  Form,
  SuccessActions,
  SuccessIcon,
  SuccessInfo,
  SuccessTitle,
  Title,
  ZoneField,
};
